import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Heart, MessageCircle, Shield, Sparkles, Users, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'

export default function Home() {
  const { isAuthenticated } = useAuthStore()
  const { t } = useTranslation()

  const features = [
    { icon: Heart, title: t('home.feature.empathyTitle'), desc: t('home.feature.empathyDesc') },
    { icon: MessageCircle, title: t('home.feature.shareTitle'), desc: t('home.feature.shareDesc') },
    { icon: Shield, title: t('home.feature.safeSpaceTitle'), desc: t('home.feature.safeSpaceDesc') },
    { icon: Sparkles, title: t('nav.dailyLight'), desc: t('home.feature.dailyLightDesc') },
    { icon: Users, title: t('home.feature.companionTitle'), desc: t('home.feature.companionDesc') },
    { icon: MessageCircle, title: t('nav.moodMap'), desc: t('home.feature.moodMapDesc') },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t('home.versionBadge')}
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent">
              {t('home.title')}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              {t('home.heroDescription')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {isAuthenticated ? (
                <Link to="/feed">
                  <Button size="lg" className="rounded-full px-8">
                    {t('home.goToFeed')} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg" className="rounded-full px-8">
                      {t('auth.joinCommunity')} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/explore">
                    <Button size="lg" variant="outline" className="rounded-full px-8">
                      {t('nav.explore')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl font-bold mb-4">{t('home.featuresTitle')}</h2>
            <p className="text-muted-foreground">{t('home.featuresSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="p-8 md:p-12 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
            <h2 className="font-display text-3xl font-bold mb-4">{t('home.ctaTitle')}</h2>
            <p className="text-muted-foreground mb-8">
              {t('home.ctaDescription')}
            </p>
            <Link to={isAuthenticated ? '/feed' : '/register'}>
              <Button size="lg" className="rounded-full px-8">
                {isAuthenticated ? t('home.goToFeed') : t('home.ctaRegister')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
